import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiRepeat, FiZap } from 'react-icons/fi';

const PricingCards = ({ language }) => {
  const [activeTab, setActiveTab] = useState('oneTime'); // 'oneTime' or 'monthly'
  
  const oneTimePaymentPlans = [
    {
      name: "Love Days Sponsor $5",
      amount: 5,
      priceId: "price_1RuIldIEnneB4h0md240jQel",
      paymentLink: "https://buy.stripe.com/dRm7sM8yT2Gx2zldOW0Fi0a",
      currency: "usd",
      titleEn: "Sweet Crush",
      titleZh: "甜蜜心動"
    },
    {
      name: "Love Days Sponsor $8",
      amount: 8,
      priceId: "price_1RuIldIEnneB4h0m756a58Kp",
      paymentLink: "https://buy.stripe.com/eVq7sM4iD2Gx1vh5iq0Fi0b",
      currency: "usd",
      titleEn: "True Romance",
      titleZh: "真愛浪漫"
    },
    {
      name: "Love Days Sponsor $10",
      amount: 10,
      priceId: "price_1RuIldIEnneB4h0m9unSdp60",
      paymentLink: "https://buy.stripe.com/fZu9AUeXh6WN0rd5iq0Fi0c",
      currency: "usd",
      titleEn: "Eternal Love",
      titleZh: "永恆之愛"
    }
  ];

  const monthlyPaymentPlans = [
    {
      name: "Love Days Sponsor $5/mo",
      amount: 5,
      priceId: "price_monthly_5",
      paymentLink: "https://buy.stripe.com/dRm5kE8yTepf2zl4em0Fi0k",
      currency: "usd",
      interval: "monthly",
      titleEn: "Love Journey",
      titleZh: "愛的旅程"
    },
    {
      name: "Love Days Sponsor $8/mo",
      amount: 8,
      priceId: "price_monthly_8",
      paymentLink: "https://buy.stripe.com/eVq7sMg1lgxnfm79yG0Fi0l",
      currency: "usd",
      interval: "monthly",
      titleEn: "Devoted Heart",
      titleZh: "摯愛之心"
    },
    {
      name: "Love Days Sponsor $10/mo",
      amount: 10,
      priceId: "price_monthly_10",
      paymentLink: "https://buy.stripe.com/5kQcN68yT1Ct4Hth180Fi0j",
      currency: "usd",
      interval: "monthly",
      titleEn: "Soulmate Support",
      titleZh: "靈魂伴侶"
    }
  ];

  const handlePayment = (paymentLink) => {
    window.open(paymentLink, '_blank');
  };

  const getFeatures = (amount, isMonthly = false) => {
    const baseFeatures = [
      language === 'zh' ? '支持開發者' : 'Support Developer',
      language === 'zh' ? '幫助改進應用' : 'Help Improve App'
    ];

    if (amount >= 8) {
      baseFeatures.push(language === 'zh' ? '優先功能建議' : 'Priority Feature Requests');
    }

    if (amount >= 10) {
      baseFeatures.push(language === 'zh' ? '特別感謝' : 'Special Thanks');
    }

    // Add monthly-specific feature
    if (isMonthly) {
      baseFeatures.push(language === 'zh' ? '持續支持' : 'Ongoing Support');
    }

    return baseFeatures;
  };

  const activePlans = activeTab === 'oneTime' ? oneTimePaymentPlans : monthlyPaymentPlans;

  return (
    <div className="pricing-section">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-2 flex items-center justify-center">
          <span className="emoji mr-2">💖</span>
          {language === 'zh' ? '支持我們的愛情計算器' : 'Support Our Love Calculator'}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          {language === 'zh' 
            ? '如果您喜歡我們的愛情計算器，請考慮贊助我們！您的支持將幫助我們繼續改進和添加新功能。'
            : 'If you love our Love Calculator, consider sponsoring us! Your support helps us continue improving and adding new features.'
          }
        </p>
      </div>

      {/* Toggle between One-time and Monthly */}
      <div className="flex justify-center mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm border border-gray-200 dark:border-gray-700 flex">
          <button
            onClick={() => setActiveTab('oneTime')}
            className={`flex items-center px-4 py-2 rounded-md transition-all ${
              activeTab === 'oneTime' 
                ? 'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300 font-medium' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <SafeIcon icon={FiZap} className="mr-2" />
            {language === 'zh' ? '一次性贊助' : 'One-time'}
          </button>
          <button
            onClick={() => setActiveTab('monthly')}
            className={`flex items-center px-4 py-2 rounded-md transition-all ${
              activeTab === 'monthly' 
                ? 'bg-love-100 dark:bg-love-900 text-love-700 dark:text-love-300 font-medium' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <SafeIcon icon={FiRepeat} className="mr-2" />
            {language === 'zh' ? '月度贊助' : 'Monthly'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {activePlans.map((plan, index) => (
          <motion.div
            key={plan.priceId}
            className={`pricing-card bg-white dark:bg-gray-800 rounded-xl shadow-lg border-2 p-6 relative overflow-hidden ${
              plan.amount === 8 ? 'border-love-400 transform scale-105' : 'border-gray-200 dark:border-gray-700'
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            whileHover={{ scale: 1.03 }}
          >
            {plan.amount === 8 && (
              <div className="absolute top-0 left-0 right-0 bg-love-500 text-white text-center py-2 text-sm font-medium">
                <span className="emoji mr-1">⭐</span>
                {language === 'zh' ? '最受歡迎' : 'Most Popular'}
              </div>
            )}

            <div className={`text-center ${plan.amount === 8 ? 'mt-6' : ''}`}>
              <h3 className="text-lg font-bold text-love-600 dark:text-love-400 mb-2">
                {language === 'zh' ? plan.titleZh : plan.titleEn}
              </h3>
              <div className="text-4xl font-bold text-love-500 mb-1">
                ${plan.amount}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                {plan.interval === 'monthly' ? 
                  (language === 'zh' ? '每月' : '/month') : 
                  (language === 'zh' ? '一次性' : 'one-time')}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {language === 'zh' ? 
                  `${plan.interval === 'monthly' ? '月度' : ''}贊助` : 
                  `${plan.interval === 'monthly' ? 'Monthly ' : ''}Sponsor`}
              </div>

              <ul className="space-y-3 mb-6">
                {getFeatures(plan.amount, plan.interval === 'monthly').map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-gray-600 dark:text-gray-400">
                    <span className="text-love-500 mr-2">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <motion.button
                onClick={() => handlePayment(plan.paymentLink)}
                className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-300 ${
                  plan.amount === 8
                    ? 'bg-love-500 hover:bg-love-600 text-white shadow-lg'
                    : 'bg-love-100 hover:bg-love-200 dark:bg-love-900 dark:hover:bg-love-800 text-love-700 dark:text-love-300'
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label={`${language === 'zh' ? '贊助' : 'Sponsor'} ${language === 'zh' ? plan.titleZh : plan.titleEn} $${plan.amount} ${plan.interval === 'monthly' ? (language === 'zh' ? '每月' : '/month') : ''}`}
              >
                <span className="emoji mr-2">💝</span>
                {language === 'zh' ? '立即贊助' : 'Sponsor Now'}
              </motion.button>
            </div>

            {/* Decorative hearts */}
            <div className="absolute top-2 right-2 opacity-20">
              <span className="text-love-300 text-2xl">💕</span>
            </div>
            <div className="absolute bottom-2 left-2 opacity-20">
              <span className="text-love-300 text-xl">💖</span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="text-center mt-8">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          <span className="emoji mr-1">🔒</span>
          {language === 'zh' 
            ? '安全支付由 Stripe 提供支持'
            : 'Secure payments powered by Stripe'
          }
        </p>
      </div>
    </div>
  );
};

export default PricingCards;